import { useContext } from 'react';
import { T, useGT } from 'gt-react';
import {
  CaretUpDownIcon,
  CheckIcon,
  BuildingsIcon,
} from '@phosphor-icons/react';
import { useOrganizationLogo } from '@/hooks/organization-config/useOrganizationLogo';
import { AuthContext } from '@/context/auth.context';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Product } from '@/components/common/semantic-icons';

interface TeamSwitcherProps {
  brandName: string;
  className?: string;
}

export function TeamSwitcher({
  brandName: _brandName,
  className,
}: TeamSwitcherProps) {
  const gt = useGT();
  const { isMobile } = useSidebar();
  const { logoUrl, organizationName } = useOrganizationLogo();
  const { currentUser, switchOrganization, isSwitchingOrganization } =
    useContext(AuthContext);

  const availableOrganizations = currentUser?.availableOrganizations ?? [];
  // Switching orgs is a write, and the imitation guard refuses it server-side.
  // Collapsing the switcher to a plain label is better than offering a menu
  // whose every entry answers with "this is only a view mode" — to imitate a
  // user in another org, switch org first, then imitate.
  const hasMultipleOrgs =
    availableOrganizations.length > 1 && !currentUser?.imitation;

  const menuButton = (
    <SidebarMenuButton
      size="lg"
      className="data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
    >
      <div className="bg-white text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={gt('{organizationName} Logo', {
              context: 'sidebar organization logo alt text',
              organizationName,
            })}
            className="size-6 rounded object-contain"
          />
        ) : (
          <Product className="size-4" />
        )}
      </div>
      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
        {/* eslint-disable i18next/no-literal-string -- product brand name, not translatable */}
        <span className="truncate font-medium">Gently AI</span>
        {/* eslint-enable i18next/no-literal-string */}
        <span className="truncate text-xs">{organizationName}</span>
      </div>
      {hasMultipleOrgs && (
        <CaretUpDownIcon className="ml-auto group-data-[collapsible=icon]:hidden" />
      )}
    </SidebarMenuButton>
  );

  return (
    <SidebarMenu className={className}>
      <SidebarMenuItem>
        {hasMultipleOrgs ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{menuButton}</DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--anchor-width) min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                <T context="sidebar org switcher section label">
                  Organizations
                </T>
              </DropdownMenuLabel>
              {availableOrganizations.map((org) => (
                <DropdownMenuItem
                  key={org.id}
                  disabled={isSwitchingOrganization}
                  onClick={() => {
                    if (org.id !== currentUser?.organizationId) {
                      switchOrganization(org.id);
                    }
                  }}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-md border">
                    <BuildingsIcon className="size-3.5 shrink-0" />
                  </div>
                  <span className="flex-1 truncate">{org.name}</span>
                  {org.id === currentUser?.organizationId && (
                    <CheckIcon className="ml-2 h-4 w-4" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          menuButton
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
